// search-fold — the ONE locale-aware fold under every search: the shared word
// rules (`search-rules.ts` — the browser's matcher and the server's builder) and
// the topic picker's and the workbench's word-start matcher (`matchesQuery`) all fold through it,
// so the screen, the exported file and the database agree on what a typed text
// matches (ADR-072 §1 · ADR-045 — the file mirrors the current view). Folds
// case, Latin accents (é→e), the common Arabic variants (harakat, tatweel,
// alef/yaa/taa-marbuta) and the native digits (٠–٩ · ۰–۹ → 0–9) so "khobar" /
// "ecole" / "الخبر" / "GU٤٣٥" match without diacritics and in either digit
// script. `search_fold()` in the baseline schema is its SQL twin (db/search.test.ts
// pins the pair on a corpus) — the generated search columns and the browser
// fold one way. Sort collation is a separate concern — matching only.
// Harakat (fatha…sukun, U+064B–U+065F) + superscript alef (U+0670) + tatweel
// (U+0640) — all dropped. NOT a U+064B–U+0670 span: that would swallow the
// Arabic-Indic digits ٠–٩ (U+0660–U+0669), which are folded to 0–9 below,
// never dropped.
const ARABIC_MARKS = /[\u064B-\u065F\u0670\u0640]/g;
// Combining marks left after NFKD decomposition (folds Latin accents).
const COMBINING_MARKS = /[̀-ͯ]/g;
// The native digits — Arabic-Indic ٠–٩ (U+0660–U+0669) and Persian ۰–۹
// (U+06F0–U+06F9) — are ONE alphabet with 0–9 for MATCHING (ADR-072 §1): a
// reference typed GU٤٣٥ finds GU435, a Western 45 finds a name holding ٤٥.
// NFKD does not decompose them (the fullwidth digits it does), so they are
// mapped by hand. Text is never rewritten by this — a name is stored as typed;
// what is SHOWN is ADR-030's numerals axis.
const NATIVE_DIGITS = /[\u0660-\u0669\u06F0-\u06F9]/g;
const westernDigit = (ch) => String(ch.charCodeAt(0) - (ch.charCodeAt(0) >= 0x06f0 ? 0x06f0 : 0x0660));
/** The native digits of a text as 0–9, everything else untouched — the fold's
 *  digit step alone, for a text read BEFORE the fold (the tickets list reads
 *  `#١٢٨` through it, then its glyph). */
export function foldDigits(value) {
    return value.replace(NATIVE_DIGITS, westernDigit);
}
/** Normalize for accent-, diacritic-, case- and digit-script-insensitive matching. */
export function normalizeForSearch(value) {
    return value
        .normalize('NFKD')
        .replace(NATIVE_DIGITS, westernDigit)
        .replace(COMBINING_MARKS, '')
        .replace(ARABIC_MARKS, '')
        .replace(/[آأإٱ]/g, 'ا') // آ أ إ ٱ → ا
        .replace(/ى/g, 'ي') // ى → ي
        .replace(/ة/g, 'ه') // ة → ه
        .toLowerCase()
        .trim();
}
// ── the files library's search contract (ems-app FRONTEND-PATTERNS §Files) ──
// The query splits on whitespace into TOKENS; a candidate matches when EVERY
// token starts a word in SOME of its searchable fields, under the same fold —
// so "rectifier battery" finds the rectifier photo that names the battery
// string, and "rect" already finds "Rectifier". AND across tokens, OR across
// fields (the search-box convention people carry from Drive and Photos); never
// a phrase-only substring of the joined fields. The topic picker and the workbench
// harness narrow a small loaded list with it; the files library itself searches on
// the server's one builder (db/search.ts — ADR-072).
export function searchTokens(query) {
    return normalizeForSearch(query).split(/\s+/).filter(Boolean);
}
/** The words the fields offer — letters and digits, split on everything else
 *  ("IMG_3101" → "img", "3101"; "RCT-01" → "rct", "01"). */
export function searchWords(fields) {
    const out = [];
    for (const f of fields) {
        if (!f)
            continue;
        for (const w of normalizeForSearch(f).split(/[^\p{L}\p{N}]+/u))
            if (w)
                out.push(w);
    }
    return out;
}
/** Every token starts some word of some field. An empty query matches everything. */
export function matchesQuery(query, fields) {
    const tokens = searchTokens(query);
    if (tokens.length === 0)
        return true;
    const words = searchWords(fields);
    return tokens.every((tok) => words.some((w) => w.startsWith(tok)));
}
